<template>
  <div>
    <p class="text-muted">
      กลุ่มหัตถการใช้ให้ AI วิเคราะห์ เช่น คนไข้ที่ถึงเวลาขูดหินปูนซ้ำ (กลุ่ม SCALING)
      หรือคนไข้ที่ยังไม่เคยทำหัตถการในกลุ่มนี้ เลือกได้ทั้งรายการหัตถการและทั้งหมวด
    </p>
    <div v-if="isLoading" class="text-center py-3"><b-spinner small /></div>
    <b-alert v-else-if="loadError" show variant="danger">
      {{ loadError }}
      <b-button size="sm" variant="outline-danger" class="ml-2" @click="load">ลองใหม่</b-button>
    </b-alert>
    <template v-else>
      <b-alert v-if="catalogTruncated" show variant="warning">
        รายการหัตถการหรือหมวดหมู่มีมากเกินกว่าที่แสดงได้ (หัตถการ 5,000 / หมวดหมู่ 2,000)
      </b-alert>

      <b-card v-for="(group, index) in groups" :key="group.uiKey" class="mb-2" body-class="py-2">
        <b-form-row>
          <b-col md="3" class="mb-2">
            <label class="small mb-0">รหัสกลุ่ม</label>
            <b-form-input
              v-model="group.code"
              size="sm"
              :readonly="isLockedScaling(group)"
              :formatter="formatCode"
              placeholder="เช่น IMPLANT"
            />
          </b-col>
          <b-col md="4" class="mb-2">
            <label class="small mb-0">ชื่อกลุ่ม</label>
            <b-form-input v-model="group.name" size="sm" maxlength="100" placeholder="เช่น รากฟันเทียม" />
          </b-col>
          <b-col md="5" class="mb-2 text-right align-self-end">
            <small v-if="isLockedScaling(group)" class="text-muted">กลุ่ม SCALING ลบไม่ได้ (เว้นว่างได้)</small>
            <b-button v-else size="sm" variant="outline-danger" @click="removeGroup(index)">
              <i class="fas fa-trash-alt mr-1"></i>ลบกลุ่ม
            </b-button>
          </b-col>
        </b-form-row>
        <b-form-row>
          <b-col md="6" class="mb-2">
            <label class="small mb-0">รายการหัตถการ</label>
            <multiselect
              v-model="group.treatments"
              :options="treatmentOptions"
              :multiple="true"
              :close-on-select="false"
              :options-limit="300"
              label="label"
              track-by="id"
              placeholder="ค้นหาหัตถการ"
              :show-labels="false"
            >
              <template #noResult>ไม่พบหัตถการ</template>
              <template #noOptions>ไม่มีรายการหัตถการ</template>
            </multiselect>
          </b-col>
          <b-col md="6" class="mb-2">
            <label class="small mb-0">หมวดหัตถการ (รวมทุกรายการในหมวด)</label>
            <multiselect
              v-model="group.categories"
              :options="categoryOptions"
              :multiple="true"
              :close-on-select="false"
              label="label"
              track-by="id"
              placeholder="ค้นหาหมวด"
              :show-labels="false"
            >
              <template #noResult>ไม่พบหมวด</template>
              <template #noOptions>ไม่มีหมวดหัตถการ</template>
            </multiselect>
          </b-col>
        </b-form-row>
      </b-card>

      <b-alert v-if="errorMessage" show variant="danger" class="mt-2">{{ errorMessage }}</b-alert>
      <b-alert v-if="savedMessage" show variant="success" dismissible class="mt-2" @dismissed="savedMessage = ''">
        {{ savedMessage }}
      </b-alert>
      <div class="d-flex justify-content-between mt-2">
        <b-button size="sm" variant="outline-primary" :disabled="groups.length >= 30" @click="addGroup">
          <i class="fas fa-plus mr-1"></i>เพิ่มกลุ่ม
        </b-button>
        <b-button size="sm" variant="primary" :disabled="isSaving" @click="save">
          <b-spinner v-if="isSaving" small class="mr-1" />บันทึกกลุ่มหัตถการ
        </b-button>
      </div>
    </template>
  </div>
</template>

<script>
import { mcpErrorMessage } from "@/components/mcp/mcpErrors";

const SCALING = "SCALING";
const CODE_PATTERN = /^[A-Z][A-Z0-9_]{1,29}$/;
let uiKeySequence = 0;

export default {
  name: "McpProcedureGroups",
  props: {
    clinicUrl: { type: String, required: true },
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      loadError: "",
      catalogTruncated: false,
      treatmentOptions: [],
      categoryOptions: [],
      groups: [],
      errorMessage: "",
      savedMessage: "",
    };
  },
  created() {
    this.load();
  },
  methods: {
    isLockedScaling(group) {
      return group.locked && group.code === SCALING;
    },
    optionsFor(options, ids, missingLabel) {
      return ids.map(id => options.find(o => o.id === id) || { id, label: `#${id} (${missingLabel})` });
    },
    toGroup(group, locked) {
      uiKeySequence += 1;
      return {
        uiKey: uiKeySequence,
        locked,
        code: group.code,
        name: group.name,
        treatments: this.optionsFor(this.treatmentOptions, group.mTreatIds || [], "หัตถการที่ถูกลบ"),
        categories: this.optionsFor(this.categoryOptions, group.mTreatCateIds || [], "หมวดที่ถูกลบ"),
      };
    },
    applyData(data) {
      // Every clinic can save groups: BE creates the automatic FREE setting on first save.
      this.catalogTruncated = Boolean(data.catalog.truncated);
      this.treatmentOptions = data.catalog.treatments.map(t => ({
        id: t.id,
        label: t.code ? `${t.nameTh} (${t.code})` : t.nameTh,
      }));
      this.categoryOptions = data.catalog.categories.map(c => ({ id: c.id, label: c.nameTh }));
      this.groups = data.procedureGroups.map(g => this.toGroup(g, true));
    },
    async load() {
      this.isLoading = true;
      this.loadError = "";
      try {
        const data = await this.$store.dispatch("moduleMcpSetting/fetchProcedureGroups", { clinicUrl: this.clinicUrl });
        this.applyData(data);
      } catch (error) {
        this.loadError = mcpErrorMessage(error, "โหลดกลุ่มหัตถการไม่สำเร็จ");
      } finally {
        this.isLoading = false;
      }
    },
    formatCode(value) {
      return String(value || "").toUpperCase().replace(/[^A-Z0-9_]/g, "");
    },
    addGroup() {
      this.groups.push(this.toGroup({ code: "", name: "", mTreatIds: [], mTreatCateIds: [] }, false));
    },
    removeGroup(index) {
      this.groups.splice(index, 1);
    },
    validate() {
      const seen = new Set();
      for (const group of this.groups) {
        if (!CODE_PATTERN.test(group.code)) {
          return `รหัสกลุ่ม “${group.code || "(ว่าง)"}” ไม่ถูกต้อง: ใช้ A–Z, 0–9 หรือ _ ยาว 2–30 ตัว และขึ้นต้นด้วยตัวอักษร`;
        }
        if (seen.has(group.code)) return `รหัสกลุ่ม ${group.code} ซ้ำกัน`;
        seen.add(group.code);
        const name = (group.name || "").trim();
        if (!name || name.length > 100) return `กรุณาระบุชื่อกลุ่ม ${group.code} (ไม่เกิน 100 ตัวอักษร)`;
      }
      if (!seen.has(SCALING)) return "ต้องมีกลุ่ม SCALING (ขูดหินปูน) เสมอ";
      return "";
    },
    async save() {
      this.errorMessage = this.validate();
      this.savedMessage = "";
      if (this.errorMessage) return;
      this.isSaving = true;
      try {
        // Only the fields declared in the BE schema (additionalProperties: false).
        const procedureGroups = this.groups.map(group => ({
          code: group.code,
          name: group.name.trim(),
          mTreatIds: group.treatments.map(t => t.id),
          mTreatCateIds: group.categories.map(c => c.id),
        }));
        const data = await this.$store.dispatch("moduleMcpSetting/saveProcedureGroups", {
          clinicUrl: this.clinicUrl,
          procedureGroups,
        });
        this.applyData(data);
        this.savedMessage = "บันทึกกลุ่มหัตถการแล้ว";
      } catch (error) {
        this.errorMessage = mcpErrorMessage(error, "บันทึกกลุ่มหัตถการไม่สำเร็จ");
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>
